/**
 * Dashboard-filter-specific helpers. Operator definitions and semantics live in
 * the shared field-type operator model so chart conditional formatting reuses
 * the exact same operator set — see SSP-4176 / SSP-3620.
 */
export { FIELD_TYPE_OPERATORS, OPERATORS_BY_FIELD_TYPE, getOperatorConfig, getOperatorLabel, getOperatorsForFieldType, isBlankOperator, isFreeTextOperator, isNumericFieldType, isRangeOperator, isTimeFieldType, normalizeOperator, operatorRequiresValue, toCanonicalFieldType, } from '../../utils/field_type_operators.utils';
const CALENDAR_EXCLUSIVE_CUBE_OPERATORS = {
    beforeDate: { operator: 'lt' },
    afterDate: { operator: 'gte', shiftDays: 1 },
};
/**
 * Extract `YYYY-MM-DD` from a Cube-style datetime string and optionally shift
 * it by whole calendar days. Handles both `2026-06-23T14:30:00Z` (ISO 8601)
 * and `2026-06-23 14:30:00` (space-separated, which Cube also returns), and
 * validates month / day ranges so a malformed value like `2026-13-45` is
 * passed through unchanged instead of silently rolling over via `Date.UTC`.
 */
const toCalendarDateValue = (value, shiftDays = 0) => {
    if (typeof value !== 'string') {
        return value;
    }
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) {
        return value;
    }
    const [dateOnly, yearStr, monthStr, dayStr] = match;
    const year = Number(yearStr);
    const month = Number(monthStr);
    const day = Number(dayStr);
    if (month < 1 || month > 12 || day < 1 || day > 31) {
        return value;
    }
    if (!shiftDays) {
        return dateOnly;
    }
    const shifted = new Date(Date.UTC(year, month - 1, day + shiftDays));
    const pad = (n) => String(n).padStart(2, '0');
    return `${shifted.getUTCFullYear()}-${pad(shifted.getUTCMonth() + 1)}-${pad(shifted.getUTCDate())}`;
};
export const normalizeCubeFilterParam = (filter) => {
    const mapping = CALENDAR_EXCLUSIVE_CUBE_OPERATORS[filter.operator];
    if (!mapping) {
        return filter;
    }
    return {
        ...filter,
        operator: mapping.operator,
        values: (filter.values ?? []).map((v) => toCalendarDateValue(v, mapping.shiftDays)),
    };
};
/** Options for one filter row, excluding fields already selected in other rows. */
export function getFilterFieldOptionsForRow(allFields, selectedKeys, currentRowKey) {
    const usedByOthers = new Set(selectedKeys.filter((key) => key && key !== currentRowKey));
    return allFields.filter((field) => !usedByOthers.has(field.key));
}
/** True when at least one dataset dimension is not yet assigned to a filter row. */
export function hasAvailableFilterFields(allFields, selectedKeys) {
    const usedKeys = new Set(selectedKeys.filter(Boolean));
    return usedKeys.size < allFields.length;
}
